import cn from 'classnames';
import { forwardRef, useMemo } from 'react';

import useSlots from '../../../utils/hooks/use-slots';
import SlotsContext from '../../../utils/slots-context';
import { Button } from '../../buttons';
import { ArrowLeftIcon } from '../../foundations';

import type { ForwardedRef, ReactElement } from 'react';

import type { SlotNames } from '../../../utils/types/slots.types';
import type { HeaderProps } from './header.types';

import './header.scss';

/**
 * The Header component is used to display a heading for a page along with a description and actions.
 *
 * ### Usage
 *
 * ```tsx
 * import { Header } from '@bloomreach/react-banana-ui';
 *
 * export default function MyCustomComponent() {
 *   return (
 *     <Header onBack={() => console.log('Go back!')}>
 *       My Page Title
 *       <Description>This is the description of header component</Description>
 *       <ActionsContainer>
 *         <Button type='tertiary'>Cancel</Button>
 *         <Button type='primary'>Save</Button>
 *       </ActionsContainer>
 *     </Header>
 *   )
 * }
 * ```
 */
const Header = forwardRef((props: HeaderProps, forwardedRef: ForwardedRef<HTMLDivElement> | null): ReactElement => {
  const {
    children,
    className,
    onBack,
    ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
  } = props;

  const headerSlotNames = useMemo(() => ['Description', 'ActionsContainer'] as SlotNames[], []);

  const { contextValue, slots } = useSlots({ slotNames: headerSlotNames });

  return (
    <SlotsContext.Provider value={contextValue}>
      <div {...rest} className={cn('rbui-header', className)} ref={forwardedRef}>
        {onBack && <Button onClick={onBack} startIcon={<ArrowLeftIcon />} type="tertiary" />}

        <div className="rbui-header__title">
          <h1 className="rbui-header__title-heading">{children}</h1>
          {slots.Description}
        </div>

        {slots.ActionsContainer}
      </div>
    </SlotsContext.Provider>
  );
});

Header.displayName = 'Header';

export default Header;
